import { useState } from "react";
import { Link } from "react-router-dom";
import type { AuditAction, AuditEntry } from "@/api/profiles";
import { ROUTES } from "@/routes";
import { fieldLabels } from "./fieldLabels";
import styles from "./Audit.module.css";

/** Human labels for each entity type. Falls back to a humanized raw value. */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
  firm_profile: "Firm profile",
  firm_logo: "Firm logo",
  lawyer_profile: "Lawyer profile",
  article: "Article",
  firm_tab: "Firm tab",
  team_page: "Team page",
  user_guide: "User guide",
};

export function entityTypeLabel(entityType: string): string {
  return (
    ENTITY_TYPE_LABELS[entityType] ??
    entityType.replace(/[_-]+/g, " ").replace(/^\w/, c => c.toUpperCase())
  );
}

export const ENTITY_TYPE_OPTIONS = [
  { value: "", label: "All entity types" },
  { value: "firm_profile", label: "Firm profile" },
  { value: "firm_logo", label: "Firm logo" },
  { value: "lawyer_profile", label: "Lawyer profile" },
  { value: "article", label: "Article" },
  { value: "firm_tab", label: "Firm tab" },
  { value: "team_page", label: "Team page" },
  { value: "user_guide", label: "User guide" },
];

export const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "created", label: "Created" },
  { value: "updated", label: "Updated" },
  { value: "published", label: "Published" },
  { value: "unpublished", label: "Unpublished" },
  { value: "deleted", label: "Deleted" },
];

/**
 * Best-effort edit-page path for the entity referenced by an audit entry.
 * Returns `null` when no dedicated edit route exists (so we render plain text).
 */
export function entityEditPath(entry: AuditEntry): string | null {
  switch (entry.entityType) {
    case "firm_profile":
      return ROUTES.profiles.firmProfileDetail(entry.entityId);
    case "firm_logo":
      return entry.companyId ? ROUTES.profiles.firmDetail(entry.companyId) : null;
    case "lawyer_profile":
      return ROUTES.profiles.lawyerProfileDetail(entry.entityId);
    case "article":
      return ROUTES.profiles.articleDetail(entry.entityId);
    case "team_page":
      return ROUTES.profiles.teamPageDetail(entry.entityId);
    case "firm_tab":
      // Firm-tab detail routes are keyed by kind; entitySubType carries it.
      return entry.entitySubType
        ? ROUTES.profiles.firmTabDetail(entry.entitySubType, entry.entityId)
        : null;
    case "user_guide":
      return ROUTES.profiles.userGuide;
    default:
      return null;
  }
}

function isValidDate(d: Date): boolean {
  return !Number.isNaN(d.getTime());
}

export function formatAbsoluteTime(iso: string): string {
  const d = new Date(iso);
  if (!isValidDate(d)) return iso;
  return d.toLocaleString();
}

/** "just now" / "5 min ago" / "3 days ago" style relative time. */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const d = new Date(iso);
  if (!isValidDate(d)) return iso;
  const diffMs = now - d.getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);
  const sec = Math.round(abs / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);

  const suffix = future ? "from now" : "ago";
  if (sec < 45) return "just now";
  if (min < 60) return `${min} min ${suffix}`;
  if (hr < 24) return `${hr} hr ${suffix}`;
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ${suffix}`;
  return d.toLocaleDateString();
}

export function WhenCell({ iso }: { iso: string }) {
  return (
    <span className={styles.when} title={formatAbsoluteTime(iso)}>
      {formatRelativeTime(iso)}
    </span>
  );
}

export function ActionPill({ action }: { action: AuditAction | string }) {
  return (
    <span className={styles.actionPill} data-action={action}>
      {action}
    </span>
  );
}

/** Maps `actor.source` (e.g. "account-bff") to a portal label + key. */
export function portalFromSource(source: string | null): { key: string; label: string } | null {
  if (!source) return null;
  const lower = source.toLowerCase();
  if (lower.includes("account")) return { key: "account", label: "Account" };
  if (lower.includes("staff")) return { key: "staff", label: "Staff" };
  return { key: "other", label: source };
}

/**
 * Renders the actor on a single line: user + portal badge (+ impersonated) and,
 * when resolvable, the originating firm name. The firm name is resolved at the
 * list level via `useCompanyNames` and passed in as `companyName` — this
 * component never calls the hook itself so it stays cheap to render per row.
 */
export function ActorCell({
  actor,
  companyName,
}: {
  actor: AuditEntry["actor"];
  companyName?: string | null;
}) {
  const portal = portalFromSource(actor.source);
  const label = actor.email ?? (actor.type === "system" ? "System" : (actor.id ?? "Unknown"));
  return (
    <div className={styles.actor}>
      <span className={styles.actorEmail} title={label}>
        {label}
      </span>
      {portal && (
        <span className={styles.badge} data-portal={portal.key}>
          {portal.label}
        </span>
      )}
      {actor.isImpersonating && (
        <span className={`${styles.badge} ${styles.badgeImpersonated}`}>impersonated</span>
      )}
      {companyName && (
        <span className={styles.actorFirm} title={companyName}>
          {companyName}
        </span>
      )}
    </div>
  );
}

/**
 * Renders the entity TYPE only (e.g. "Firm profile"), linked to the entity's
 * edit page when one exists. The raw `entityLabel` is intentionally not shown —
 * for firm profiles it is raw HTML.
 */
export function EntityCell({ entry }: { entry: AuditEntry }) {
  const path = entityEditPath(entry);
  const label = entityTypeLabel(entry.entityType);
  return path ? (
    <Link to={path} className={styles.entityLink}>
      {label}
    </Link>
  ) : (
    <span className={styles.entityTypePlain}>{label}</span>
  );
}

/**
 * Renders friendly changed-field labels. Truncates to `previewCount` and offers
 * an inline expand toggle when there are more.
 */
export function ChangedFields({
  fields,
  previewCount = 4,
}: {
  fields: readonly string[];
  previewCount?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const labels = fieldLabels(fields);

  if (labels.length === 0) {
    return <span className={styles.changedNone}>—</span>;
  }

  const shown = expanded ? labels : labels.slice(0, previewCount);
  const remaining = labels.length - shown.length;

  return (
    <span className={styles.changedFields}>
      {shown.join(", ")}
      {remaining > 0 && (
        <button
          type="button"
          className={styles.moreButton}
          onClick={() => setExpanded(true)}
          aria-label={`Show ${remaining} more changed field${remaining === 1 ? "" : "s"}`}
        >
          +{remaining} more
        </button>
      )}
    </span>
  );
}
